//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

// Module-level cache + in-flight de-duplication for per-teamspace wiki status
// lookups. Shared across components (Sidebar, WikiPage) so switching to a
// teamspace triggers at most one Control lookup, no matter how many
// components need the result, and revisiting it later is served from cache.
const cache = new Map<string, boolean>();
const inFlight = new Map<string, Promise<boolean>>();

export function getCachedWikiStatus(teamspaceId: string): boolean | undefined {
  return cache.get(teamspaceId);
}

// Test-only helper to reset shared module state between test cases.
export function clearWikiStatusCache(): void {
  cache.clear();
  inFlight.clear();
}

export function fetchWikiStatus(
  teamspaceId: string,
  // PromiseLike (not Promise) so callers can pass a Q.Promise directly without wrapping it.
  fetcher: () => PromiseLike<{ hasWiki: boolean } | undefined>,
): Promise<boolean> {
  const cached = cache.get(teamspaceId);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }

  let request = inFlight.get(teamspaceId);
  if (!request) {
    // Wrapped in Promise.resolve().then(...) so a fetcher that throws
    // synchronously is still routed through the catch below instead of
    // escaping as an unhandled exception.
    request = Promise.resolve()
      .then(() => fetcher())
      .then((res) => res?.hasWiki ?? false)
      .catch(() => false)
      .finally(() => {
        inFlight.delete(teamspaceId);
      });
    inFlight.set(teamspaceId, request);
  }

  return request.then((result) => {
    cache.set(teamspaceId, result);
    return result;
  });
}
